import { isMatch, merge, pick } from 'lodash';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { noneIf } from 'pages/Search/utils';
import normalizeSearch from 'utils/normalize-search';

interface Payload {
    vendor: string;
    product: string;
    page: number;
    size: number;
    sort?: {
        field: 'version' | 'cves';
        direction: 'asc' | 'desc';
    };
    search: string;
    year?: number;
    month?: number;
    score?: number;
}

const defaults: Payload = {
    vendor: '',
    product: '',
    page: 1,
    size: 20,
    sort: {
        field: 'cves',
        direction: 'desc'
    },
    search: '',
    year: undefined,
    month: undefined,
    score: undefined
};

export function useProductVersionsCatalogPayload() {
    const history = useHistory();
    const location = useLocation();
    const parameters = useParams<{ vendor: string; product: string; page: string }>();
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);

    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const vendor = parameters.vendor ? decodeURIComponent(parameters.vendor) : defaults.vendor;
    const product = parameters.product ? decodeURIComponent(parameters.product) : defaults.product;
    const size = query.size ? parseInt(query.size.toString()) : defaults.size;
    const search = query.search ? normalizeSearch(query.search.toString()) : defaults.search;
    const sort = query.sort || defaults.sort;
    const score = query.score ? parseInt(query.score.toString()) : defaults.score;
    const year = query.year ? parseInt(query.year.toString()) : defaults.year;
    const month = query.month ? parseInt(query.month.toString()) : defaults.month;

    const payload = useMemo(
        () =>
            ({
                page,
                vendor,
                product,
                size,
                score,
                year,
                month,
                search,
                sort
            } as Payload),
        [month, page, product, score, search, size, sort, vendor, year]
    );
    const payloadRef = usePersistent(payload);

    const url = useCallback(
        (patch: Partial<Payload>) => {
            const previous = payloadRef.current;
            const current = merge({}, previous, patch);

            return ROUTES.PRODUCT_VERSIONS({
                vendor: current.vendor,
                product: current.product,
                page: isMatch(previous, pick(current, ['vendor', 'product', 'year', 'month', 'score', 'size']))
                    ? noneIf(current.page, defaults.page)
                    : undefined,
                year: current.year,
                month: current.month,
                score: current.score,
                size: noneIf(current.size, previous.size),
                search: current.search ? noneIf(normalizeSearch(current.search), defaults.search) : undefined,
                sort: noneIf(current.sort, defaults.sort)
            });
        },
        [payloadRef]
    );

    const set = useCallback((patch: Partial<Payload>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [payload, actions] as const;
}
